/**
 * Project-scoped segment list, profile, and manage screens.
 *
 * Mounted under `/projects/:projectId/segments/*`. The list view reuses
 * `SegmentListScreen` from `/screens/Segments` in project-filtered mode.
 * The segment detail view renders the RICH `SegmentProfileScreen` from
 * `/screens/Segmentation` (Overview / Synthetic Audience / Files) by
 * resolving segments_v2.id → dashboardPageId via a quick fetch.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SegmentListScreen, SegmentManageScreen } from '@/screens/Segments';
import { SegmentProfileScreen } from '@/screens/Segmentation/SegmentProfile';
import { getAuthenticatedClient } from '@/api/client';

/** Segments list (project subtab). Filtered to the current project. The
 *  global ChatWidgetContainer lightbulb renders here via ProjectMain. */
export function ProjectSegmentsListContainer(): React.JSX.Element {
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectId = Number(projectIdParam);
  const navBase = `/projects/${projectId}/segments`;

  return (
    <SegmentListScreen
      projectId={projectId}
      navBase={navBase}
      embedded
    />
  );
}

/** Segment detail (project subtab). Renders the rich SegmentProfileScreen
 *  from the segmentation builder — Overview, Synthetic Audience, Files.
 *
 *  Route gives us the segments_v2 id, but SegmentProfileScreen works off
 *  the dashboardPageId. We do a one-shot `/segments/:id` fetch to translate,
 *  then render the profile with an override. */
export function ProjectSegmentDashboardContainer(): React.JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const segmentV2Id = idParam ? Number(idParam) : NaN;

  const [pageId, setPageId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNaN(segmentV2Id)) {
      setError('Invalid segment id');
      return;
    }
    let cancelled = false;
    getAuthenticatedClient()
      .get(`/segments/${segmentV2Id}`)
      .then((res) => {
        if (cancelled) return;
        const segment = res.data.segment as Record<string, unknown> | undefined;
        const dashboardPageId = segment?.dashboardPageId as number | null | undefined;
        if (typeof dashboardPageId === 'number') {
          setPageId(dashboardPageId);
        } else {
          setError('Segment has no dashboard page');
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load segment');
      });
    return () => {
      cancelled = true;
    };
  }, [segmentV2Id]);

  if (error) {
    return (
      <div style={{ padding: 24, color: '#991b1b' }}>Segment load failed: {error}</div>
    );
  }
  if (pageId == null) {
    return <div style={{ padding: 24, color: '#626262' }}>Loading segment…</div>;
  }

  return <SegmentProfileScreen pageIdOverride={pageId} />;
}

/** Segment manage (project subtab). Back button returns to the project's
 *  segments tab instead of the global /segments. */
export function ProjectSegmentManageContainer(): React.JSX.Element {
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectId = Number(projectIdParam);
  const backTo = `/projects/${projectId}/segments`;

  return <SegmentManageScreen backTo={backTo} />;
}
